import { Fragment } from 'react';
import { useLocation } from 'react-router';

import type { ApiUserEnvironmentOrgMini } from '#/api/userEnvironment/types/ApiUserEnvironmentOrgMini';
import { Box } from '#/components/Box/Box';
import { Divider } from '#/components/Divider/Divider';
import { FormSection } from '#/components/Form/FormSection';
import { MultiStepFormBaseHeader } from '#/components/Form/MultiStepForm/components/MultiStepFormBaseHeader';
import { Icon } from '#/components/Icon/Icon';
import { StyledVersionedRouterLink } from '#/components/Link/StyledVersionedRouterLink';
import { BodySmall } from '#/components/Text/BodySmall';
import { HeadingSmall } from '#/components/Text/HeadingSmall';
import { CartDropdownButtonContainer } from '#/containers/CartDropdownButton/CartDropdownButtonContainer';
import { useUser } from '#/contexts/UserEnvironmentContext';
import { useI18n } from '#/i18n/hooks/useI18n';
import { LayoutCenteredBody } from '#/layouts/Layout/LayoutCenteredBody';
import type { OrgDashboardApiOrg } from '#/modules/orgDashboard/api/types/OrgDashboardApiOrg';
import { useAppRouteSearchParams } from '#/routing/hooks/useAppRouteSearchParams';
import { createOrgRoute, postAListingRoute } from '#/routing/routes';
import { colors } from '#/theme/theme';

import { PostAListingPageButton } from './Button/PostAListingPageButton';
import { PostAListingPageHolder } from './PostAListingPage.styled';
import { PostAListingPageChangeEntity } from './PostAListingPageChangeEntity';
import { PostAListingPageOrgList } from './PostAListingPageOrgList';
import { usePostAListingPageButtons } from './usePostAListingPageButtons';

type Props = {
  org?: OrgDashboardApiOrg;
  orgs?: ApiUserEnvironmentOrgMini[];
};

export function PostAListingPage({ orgs, org }: Props) {
  const { i18n } = useI18n();
  const location = useLocation<{ from?: string }>();
  const { user } = useUser();
  const [searchParams] = useAppRouteSearchParams(postAListingRoute);

  const hasSingleEntity = user ? user.orgs.length === 1 : false;
  const from = location.state ? location.state.from : null;

  const postListingButtons = usePostAListingPageButtons({ org });

  const sections = [
    orgs && orgs.length > 0 ? (
      <Fragment key="orgs">
        <HeadingSmall mt={48} color={colors.lightContentGrey}>
          {i18n('Organizations')}
        </HeadingSmall>

        <Box mt={24}>
          <PostAListingPageOrgList
            orgs={orgs}
            from={from}
            listingType={
              searchParams.listingType as
                | 'JOB'
                | 'INTERNSHIP'
                | 'VOLOP'
                | 'EVENT'
            }
          />
        </Box>

        <Box mt={48}>
          <StyledVersionedRouterLink
            qaId="org-link-create-new"
            to={createOrgRoute.with({})}
          >
            {'+ '}
            {
              /* TRANSLATORS: On post a listing page */
              i18n('Create a new organization')
            }
          </StyledVersionedRouterLink>
        </Box>
      </Fragment>
    ) : (
      <Fragment key="create-org">
        <BodySmall mb={24}>
          {i18n(
            `Nonprofits, government agencies, recruitment firms, consultants/vendors to nonprofits,
                and social enterprises are welcome to join Idealist. {{link}}Visit our help page{{/link}}
                to learn more about qualifying organizations.`,
            {
              link: (
                // oxlint-disable jsx-a11y/anchor-has-content
                <a href="https://www.idealist.org/en/help/what-organizations-can-post-on-idealist" />
              ),
            },
          )}
        </BodySmall>
        <PostAListingPageButton
          type="link-with-state"
          to={createOrgRoute.with({})}
          qaId="org-link-create-new"
          fromVM={false}
        >
          {i18n('Create an organization')}
        </PostAListingPageButton>
      </Fragment>
    ),
  ];

  return (
    <PostAListingPageHolder>
      <LayoutCenteredBody>
        <MultiStepFormBaseHeader
          trackingTitle="Create a New Listing"
          title={i18n('Create a New Listing')}
          cartDropdownButton={<CartDropdownButtonContainer />}
          otherLinks={
            org
              ? [
                  <PostAListingPageChangeEntity
                    key={org.id}
                    name={org.name}
                    hasSingleEntity={hasSingleEntity}
                    from={from}
                  />,
                ]
              : undefined
          }
        />

        {org ? (
          <Box position="relative">
            <FormSection
              large
              title={
                <Box display="flex" justifyContent="space-between">
                  {i18n('Listing Type')}

                  <StyledVersionedRouterLink to={`/admin/${org.id}`}>
                    <Box display="flex" alignItems="center">
                      <Icon name="list" size={16} />
                      <BodySmall my={0} ml="8px" styleAs="h5">
                        {i18n('Manage Listings')}
                      </BodySmall>
                    </Box>
                  </StyledVersionedRouterLink>
                </Box>
              }
              description={
                <BodySmall>
                  {
                    /* TRANSLATORS message in Listing Type selection */
                    i18n(
                      `Thank you for listing your opportunities with Idealist. With more than 200,000 organizations and 2 million monthly visitors, Idealist helps people all over the world move from intention to action.`,
                    )
                  }
                </BodySmall>
              }
            >
              <Box mt={48}>
                {postListingButtons.map((button) => (
                  <PostAListingPageButton
                    key={button.type}
                    type="link-with-state"
                    to={button.url}
                    qaId={`create-listing-type-${button.type}`}
                    fromVM={false} // Do not display the "Imported Organization" tag when selecting the listing type
                  >
                    {button.label}
                  </PostAListingPageButton>
                ))}
              </Box>
            </FormSection>
          </Box>
        ) : (
          <FormSection
            large
            title={
              orgs && orgs.length > 0
                ? i18n('Select an Organization to Post')
                : i18n('Create an Organization to Post')
            }
            description={
              orgs && orgs.length > 0 ? (
                <BodySmall>
                  {i18n(`To post a new listing, choose your organization.
                    If the organization isn’t listed, create a new one, or check with
                    your organization administrators for the necessary admin access.`)}
                </BodySmall>
              ) : (
                <BodySmall mb={-48}>
                  {i18n(`To post a listing, first create a new
                    organization profile, or check with your organization
                    administrators for the necessary admin access.
                  `)}
                </BodySmall>
              )
            }
          >
            {sections.map((section, index) => (
              // TODO: Fix this the next time the file is edited.
              // oxlint-disable-next-line react/no-array-index-key
              <Fragment key={index}>
                {index > 0 && <Divider marginTop={48} marginBottom={40} />}
                {section}
              </Fragment>
            ))}
          </FormSection>
        )}
      </LayoutCenteredBody>
    </PostAListingPageHolder>
  );
}
